import React from 'react';
import {ApolloClient, NormalizedCacheObject} from '@apollo/client';
import {Outlet, useLocation, useParams} from 'react-router-dom';
import {Container} from '@mui/material';
import _ from 'lodash';
import {useGetMyYouTubeProfiles} from '../../api/graphql_profiles';
import {usePageTitle} from '../../hooks/usePageTitle';
import {BackLink} from './BackLink';

interface Props {
  client: ApolloClient<NormalizedCacheObject>;
}

/**
 * Wrapper for everything under `/profile/:pid`.
 *
 * The 5-tab nav (Report / Feed / History / Channels / Settings) is gone.
 * The home (`/profile/:pid`) is now a single-page summary, and the three
 * remaining drill-ins (Feed / History / Channels) plus detail pages get a
 * compact "← back to {profile name}" breadcrumb instead. Section nav lives
 * in the home's drill-in cards, not in this header.
 */
const ProfileLayout: React.FC<Props> = ({client}) => {
  const {pid} = useParams();
  const {pathname} = useLocation();
  const {data} = useGetMyYouTubeProfiles(client);

  const profile = React.useMemo(() => {
    const list = _.compact(data?.getMyYouTubeProfiles ?? []);
    return list.find(p => p.id === pid) ?? null;
  }, [data, pid]);

  // Home (= /profile/:pid exactly) renders its own header strip, so the
  // layout's breadcrumb would double-up. Anywhere deeper gets the back link.
  const isHome = pathname === `/profile/${pid}` || pathname === `/profile/${pid}/`;
  // Setup is the birthdate-required gate — no back link, parent
  // either saves or stays. (The gate in ProfileLayoutResponsive
  // bounces them right back if they navigate away.)
  const isSetup = pathname.startsWith(`/profile/${pid}/setup`);

  const sectionLabel = React.useMemo(() => {
    if (pathname.includes('/video/')) return profile?.name || 'Profile';
    if (pathname.includes('/channel/')) return profile?.name || 'Profile';
    if (pathname.endsWith('/feed')) return `${profile?.name || 'Profile'} · Feed`;
    if (pathname.endsWith('/history')) return `${profile?.name || 'Profile'} · History`;
    if (pathname.endsWith('/channels')) return `${profile?.name || 'Profile'} · Channels`;
    if (pathname.endsWith('/feedback')) return `${profile?.name || 'Profile'} · Feedback`;
    return profile?.name || 'Profile';
  }, [pathname, profile?.name]);

  usePageTitle(sectionLabel);

  return (
    <div>
      {!isHome && !isSetup && (
        // `md` matches the width every profile-scoped page body
        // uses (home, review, actions, feedback, history, feed,
        // channels). Breadcrumb sat at default `lg` before, which
        // made the back link visibly wider than the content.
        <Container maxWidth="md" sx={{pt: 2}}>
          <BackLink
            to={`/profile/${pid}`}
            label={profile?.name ? `back to ${profile.name}` : 'back to profile'}
          />
        </Container>
      )}

      {/* No outer Container here — every page renders its own
          (with its own maxWidth + padding choices). Wrapping in
          one would force every nested page-Container to be at
          most `lg`, defeating the per-page width control. */}
      <main>
        <Outlet />
      </main>
    </div>
  );
};

export default ProfileLayout;
